import { FormEvent, useEffect, useRef, useState } from 'react';

import { unlockProtectedLocation } from '@/arise/api';
import { loadLocation } from '@/state/features/app';
import { useAppDispatch } from '@/state/store';

import * as styles from './styles.module.scss';

interface ProtectedLocationGateProps {
    locationId: string;
}

// Shown in place of a location that is missing from the public static data because it is
// password protected in the CMS. A correct password sets the instance session cookie (the same
// one the whole-site gate uses), after which the location is fetched from /protected/locations/:id.
//
// Markup, copy and styling intentionally mirror the standalone sign-in page served by the Pages
// Function for the site-wide gate (functions/_middleware.ts → loginHtml), so both prompts look the same.
export default function ProtectedLocationGate({ locationId }: ProtectedLocationGateProps) {
    const dispatch = useAppDispatch();
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    async function submit(event: FormEvent) {
        event.preventDefault();
        if (!password || isSubmitting) return;
        setIsSubmitting(true);
        setError(null);
        try {
            const unlocked = await unlockProtectedLocation(password);
            if (unlocked) {
                dispatch(loadLocation(locationId));
                return;
            }
            setError('Incorrect password. Please try again.');
        } catch {
            setError('An error occurred. Please try again.');
        }
        setIsSubmitting(false);
    }

    return (
        <div className={styles.ProtectedLocationGate}>
            <div className={styles.card}>
                <h1 className={styles.title}>Sign In</h1>
                <form onSubmit={submit}>
                    <label className={styles.label} htmlFor="protected-location-password">
                        Password
                    </label>
                    <input
                        id="protected-location-password"
                        ref={inputRef}
                        className={styles.input}
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <button type="submit" className={styles.button} disabled={isSubmitting}>
                        {isSubmitting ? 'Signing in…' : 'Continue'}
                    </button>
                    {error && <p className={styles.error}>{error}</p>}
                </form>
            </div>
        </div>
    );
}
